import Modal from '@/components/Modal/Modal';
import { mapConfigToOptions } from '@/lib/listOptions';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import Markets from '@/constants/Markets';
import { fetchFilters, postMarketsAttach } from '@/lib/api';
import SearchTextField from '@/components/SearchTextField/SearchTextField';
import List from '@/components/List/List';
import Button from '@/components/Button/Button';
import { FormattedMessage } from 'react-intl';
import SaveIcon from '@/assets/save-floppy-disk.svg?react';
import { styled } from '@mui/material/styles';
import { AxiosResponse } from 'axios';
import { MarketSchema } from '@/lib/types';

const AddMarketModal = ({
  open,
  onClose,
  partnerId,
  partnerMarkets,
}: {
  open: boolean;
  partnerId?: string;
  onClose: () => void;
  partnerMarkets: MarketSchema[];
}) => {
  const [value, setValue] = useState<(string | number)[]>([]);
  const [search, setSearch] = useState('');
  const { isPending, data } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });
  const marketItems = mapConfigToOptions(Markets, data?.markets, {
    id: 'market_id',
    name: 'market_name',
  });
  const filteredItems = marketItems.filter(
    (item) =>
      !partnerMarkets.some((market) => market.id === item.value) &&
      item.value
        .toString()
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const { mutateAsync } = useMutation({
    mutationFn: postMarketsAttach,
  });

  const handleSave = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promises: Promise<AxiosResponse<MarketSchema, any>>[] = [];
    value.forEach((marketId) => {
      promises.push(
        mutateAsync({
          market_id: Number(marketId),
          partner_id: Number(partnerId),
        }),
      );
    });
    Promise.all(promises).then(() => {
      handleClose();
    });
  };

  const handleClose = () => {
    setValue([]);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add Market"
      actions={[
        <Button
          key="close"
          variant="text"
          onClick={handleClose}
          aria-label="Cancel Button"
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>,
        <Button
          key="save"
          variant="contained"
          disabled={!value.length}
          onClick={handleSave}
          aria-label="Save Button"
        >
          <StyledSaveIcon disabled={!value.length} />
          <FormattedMessage id="save" defaultMessage="Save" />
        </Button>,
      ]}
    >
      <SearchTextField
        value={search}
        onChange={(newV) => setSearch(newV)}
      />
      <List
        loading={isPending}
        items={filteredItems}
        values={value}
        onChange={(value) => setValue(value)}
      />
    </Modal>
  );
};

const StyledSaveIcon = styled(SaveIcon)<{ disabled: boolean }>(
  ({ theme, disabled }) => ({
    path: {
      transition: 'all 0.2s ease-in-out',
      stroke: disabled
        ? theme.palette.text.disabled
        : theme.palette.white,
    },
  }),
);

export default AddMarketModal;
